// One car, end to end: what it is, what it has cost, and what happened when
// it sold.
//
// The money panel is the point of the screen. On our own car it reads
// straight — paid, spent, sold, kept. On someone else's it has to say the
// harder thing: the buyer paid ₹9.4L, ₹9L of that is the owner's and is only
// passing through us, and CAP's ₹40k is the whole of what this sale was worth.
// Showing one "sale" figure would flatter every commission car by lakhs.

import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  FiAlertCircle,
  FiArrowLeft,
  FiCheckCircle,
  FiDollarSign,
  FiEdit3,
  FiKey,
  FiPlus,
  FiRotateCcw,
  FiTag,
  FiTrash2,
  FiTrendingUp,
  FiUser,
} from 'react-icons/fi';
import MainLayout from '../components/layout/MainLayout.jsx';
import { Alert, Button, DeleteConfirm, PageHeader } from '../components/common/index.js';
import {
  Field,
  Modal,
  NumberInput,
  StatCard,
  errText,
  fmtMoney,
  inputCls,
} from '../components/cars/controls.jsx';
import SellCarModal from '../components/cars/SellCarModal.jsx';
import AddCarModal from '../components/cars/AddCarModal.jsx';
import { carsApi } from '../api/carsApi';
import { ROUTES } from '../constants/app.constants';

const today = () => new Date().toISOString().slice(0, 10);

const Detail = ({ label, value }) => (
  <div>
    <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">{label}</p>
    <p className="text-sm font-semibold text-slate-800">{value || '—'}</p>
  </div>
);

// ── Money spent on the car ───────────────────────────────────────────────────
const ExpenseModal = ({ open, onClose, car, onSaved }) => {
  const [form, setForm] = useState({ title: '', amount: '', spent_on: today(), note: '' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) { setForm({ title: '', amount: '', spent_on: today(), note: '' }); setError(''); }
  }, [open]);

  const submit = async () => {
    setError('');
    if (!form.title.trim() || !form.amount) { setError('What was spent, and how much?'); return; }
    setBusy(true);
    try {
      const { data } = await carsApi.addExpense(car.id, form);
      onSaved(data);
      onClose();
    } catch (e) {
      setError(errText(e, 'Could not add it.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} title="Money spent on this car"
           subtitle="Service, denting, RC transfer, insurance — it comes off what the car earns.">
      {error && <Alert type="error" message={error} className="mb-4" />}
      <div className="space-y-3">
        <Field label="What for?" required>
          <input type="text" value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} placeholder="Denting & painting" className={inputCls} />
        </Field>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Field label="Amount" required>
            <NumberInput value={form.amount} onChange={(v) => setForm((f) => ({ ...f, amount: v }))} placeholder="18000" />
          </Field>
          <Field label="On" required>
            <input type="date" value={form.spent_on} onChange={(e) => setForm((f) => ({ ...f, spent_on: e.target.value }))} className={inputCls} />
          </Field>
        </div>
        <Field label="Note">
          <input type="text" value={form.note} onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))} className={inputCls} />
        </Field>
      </div>
      <div className="mt-5 flex justify-end gap-2">
        <Button variant="secondary" onClick={onClose} disabled={busy}>Cancel</Button>
        <Button onClick={submit} loading={busy}>Add it</Button>
      </div>
    </Modal>
  );
};

// ── The screen ───────────────────────────────────────────────────────────────
const CarDetailBoard = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [car, setCar] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState('');
  const [sellOpen, setSellOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [catalog, setCatalog] = useState([]);
  const [expenseOpen, setExpenseOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);   // 'car' | 'sale'
  const [deleteError, setDeleteError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await carsApi.getDetail(id);
      setCar(data);
      setError('');
    } catch (e) {
      setError(errText(e, 'Could not load this car.'));
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  // Only needed by the correction form's car picker.
  useEffect(() => {
    carsApi.getCatalog()
      .then(({ data }) => setCatalog(Array.isArray(data) ? data : data?.brands || []))
      .catch(() => setCatalog([]));   // the picker still takes typed cars
  }, []);

  const patchSale = async (payload, key) => {
    setBusy(key);
    setError('');
    try {
      const { data } = await carsApi.updateSale(car.sale.id, payload);
      setCar(data);
    } catch (e) {
      setError(errText(e, 'Could not update the sale.'));
    } finally {
      setBusy('');
    }
  };

  const removeExpense = async (expenseId) => {
    setBusy(`expense-${expenseId}`);
    try {
      const { data } = await carsApi.removeExpense(car.id, expenseId);
      setCar(data);
    } catch (e) {
      setError(errText(e, 'Could not remove it.'));
    } finally {
      setBusy('');
    }
  };

  const doDelete = async ({ password, reason }) => {
    setBusy('delete');
    setDeleteError('');
    try {
      if (confirmDelete === 'sale') {
        const { data } = await carsApi.undoSale(car.sale.id, { password, reason });
        setCar(data);
        setConfirmDelete(null);
      } else {
        await carsApi.remove(car.id, { password, reason });
        navigate(ROUTES.CARS);
      }
    } catch (e) {
      setDeleteError(errText(e, 'Could not delete.'));
    } finally {
      setBusy('');
    }
  };

  if (loading) {
    return (
      <div className="space-y-3">
        <div className="h-24 animate-pulse rounded-2xl bg-white" />
        <div className="h-64 animate-pulse rounded-2xl bg-white" />
      </div>
    );
  }

  if (!car) {
    return (
      <>
        <Alert type="error" message={error || 'Car not found.'} />
        <Button variant="secondary" className="mt-4" onClick={() => navigate(ROUTES.CARS)}>
          <FiArrowLeft className="mr-1.5 h-4 w-4" /> Back to the lot
        </Button>
      </>
    );
  }

  const pnl = car.pnl || {};
  const sale = car.sale;
  const consignment = car.ownership === 'consignment';

  return (
    <>
      <PageHeader
        icon={FiKey}
        title={car.display_name}
        purpose={consignment
          ? `${car.owner_name}'s car — CAP earns ${car.commission_label.toLowerCase()}.`
          : 'Our car — CAP keeps whatever it sells for above its cost.'}
        breadcrumb={[{ label: car.display_name }]}
        meta={[car.registration_number, car.variant_label, car.status_label,
               `taken in ${car.acquired_on_display}`].filter(Boolean).join(' · ')}
      >
        <Button variant="secondary" onClick={() => navigate(ROUTES.CARS)}>
          <FiArrowLeft className="mr-1.5 h-4 w-4" /> The lot
        </Button>
        <Button variant="secondary" onClick={() => setEditOpen(true)}>
          <FiEdit3 className="mr-1.5 h-4 w-4" /> Edit details
        </Button>
        {!sale && car.status !== 'returned' && (
          <Button onClick={() => setSellOpen(true)}>
            <FiTag className="mr-1.5 h-4 w-4" /> Sell this car
          </Button>
        )}
      </PageHeader>

      {error && <Alert type="error" message={error} className="mb-4" />}

      {/* Where the money stands */}
      <div className="mb-5 grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
        {!consignment && (
          <StatCard icon={FiDollarSign} tone="amber" label="CAP paid" value={fmtMoney(car.purchase_price)} />
        )}
        <StatCard
          icon={FiEdit3}
          tone="slate"
          label="Spent on it"
          value={fmtMoney(pnl.spent_on_car)}
          hint={`${(car.expenses || []).length} item(s)`}
        />
        <StatCard
          icon={FiTag}
          tone="sky"
          label={sale ? 'Buyer paid' : 'Asking'}
          value={fmtMoney(sale ? sale.sold_price : car.asking_price)}
          hint={consignment && sale ? `${car.owner_name} gets ${fmtMoney(sale.owner_payout)}` : undefined}
        />
        <StatCard
          icon={FiTrendingUp}
          tone={sale ? 'emerald' : 'slate'}
          label={sale ? 'CAP earned' : 'CAP would earn'}
          value={fmtMoney(sale ? pnl.cap_earning : pnl.expected_earning)}
          hint={sale ? undefined : 'if it goes at the asking price'}
        />
      </div>

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-3">
        {/* The car itself */}
        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm lg:col-span-2">
          <h2 className="mb-4 text-sm font-black uppercase tracking-widest text-slate-400">The car</h2>
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
            <Detail label="Brand" value={car.brand} />
            <Detail label="Model" value={car.model} />
            <Detail label="Trim" value={car.trim} />
            <Detail label="Year" value={car.year} />
            <Detail label="Variant" value={car.variant_label} />
            <Detail label="Registration" value={car.registration_number} />
            <Detail label="Colour" value={car.color} />
            <Detail label="KM driven" value={car.km_driven ? Number(car.km_driven).toLocaleString('en-IN') : ''} />
            <Detail label="Owners" value={car.owners_count} />
            <Detail label="RC" value={car.rc_status === 'with_rc' ? 'With RC' : 'Without RC'} />
            <Detail label="Insurance till" value={car.insurance_valid_till} />
            <Detail label="Added by" value={car.added_by} />
          </div>
          {consignment && (
            <div className="mt-4 rounded-xl border border-violet-200 bg-violet-50 p-3">
              <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-wide text-violet-500">
                <FiUser className="h-3.5 w-3.5" /> Owner
              </p>
              <div className="mt-2 grid grid-cols-2 gap-4 sm:grid-cols-4">
                <Detail label="Name" value={car.owner_name} />
                <Detail label="Phone" value={car.owner_phone} />
                <Detail label="Their price" value={fmtMoney(car.owner_price)} />
                <Detail label="CAP's cut" value={car.commission_label} />
              </div>
            </div>
          )}
          {car.notes && (
            <p className="mt-4 rounded-xl bg-slate-50 px-3 py-2 text-sm text-slate-600">{car.notes}</p>
          )}
        </div>

        {/* The sale */}
        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <h2 className="mb-4 text-sm font-black uppercase tracking-widest text-slate-400">The sale</h2>

          {!sale && (
            <div className="py-6 text-center">
              <p className="text-sm font-bold text-slate-600">Not sold yet</p>
              <p className="mt-1 text-xs text-slate-400">
                {car.status === 'returned'
                  ? 'This car went back to its owner.'
                  : 'Record the sale here when it goes.'}
              </p>
              {car.status !== 'returned' && (
                <Button className="mt-4" onClick={() => setSellOpen(true)}>
                  <FiTag className="mr-1.5 h-4 w-4" /> Sell this car
                </Button>
              )}
            </div>
          )}

          {sale && (
            <div className="space-y-3 text-sm">
              <div className="grid grid-cols-2 gap-3">
                <Detail label="Buyer" value={sale.customer_name} />
                <Detail label="Phone" value={sale.customer_phone} />
                <Detail label="Sold on" value={sale.sold_at_display} />
                <Detail label="Mode" value={sale.payment_mode} />
              </div>

              <div className="rounded-xl bg-slate-50 p-3">
                <div className="flex items-center justify-between">
                  <span className="text-slate-500">Buyer paid</span>
                  <span className="font-bold tabular-nums text-slate-800">{fmtMoney(sale.sold_price)}</span>
                </div>
                {consignment && (
                  <div className="mt-1.5 flex items-center justify-between">
                    <span className="text-slate-500">{car.owner_name}'s share</span>
                    <span className="font-semibold tabular-nums text-slate-600">{fmtMoney(sale.owner_payout)}</span>
                  </div>
                )}
                <div className="mt-1.5 flex items-center justify-between border-t border-slate-200 pt-1.5">
                  <span className="font-bold text-emerald-700">CAP keeps</span>
                  <span className="font-black tabular-nums text-emerald-700">{fmtMoney(pnl.cap_earning)}</span>
                </div>
              </div>

              {/* Anything still open, with the button that closes it */}
              {sale.balance_due !== '0.00' && (
                <div className="rounded-xl border border-amber-200 bg-amber-50 p-3">
                  <p className="flex items-center gap-2 text-xs font-bold text-amber-700">
                    <FiAlertCircle className="h-3.5 w-3.5" />
                    {fmtMoney(sale.balance_due)} still owed by the buyer
                  </p>
                  <Button
                    size="sm"
                    variant="success"
                    className="mt-2"
                    loading={busy === 'balance'}
                    onClick={() => patchSale({ balance_paid: true }, 'balance')}
                  >
                    <FiCheckCircle className="mr-1.5 h-3.5 w-3.5" /> Balance collected
                  </Button>
                </div>
              )}

              {consignment && !sale.owner_paid && (
                <div className="rounded-xl border border-rose-200 bg-rose-50 p-3">
                  <p className="flex items-center gap-2 text-xs font-bold text-rose-700">
                    <FiAlertCircle className="h-3.5 w-3.5" />
                    {fmtMoney(sale.owner_payout)} of {car.owner_name}'s money is still with us
                  </p>
                  <Button
                    size="sm"
                    variant="success"
                    className="mt-2"
                    loading={busy === 'owner'}
                    onClick={() => patchSale({ owner_paid: true }, 'owner')}
                  >
                    <FiCheckCircle className="mr-1.5 h-3.5 w-3.5" /> Owner paid
                  </Button>
                </div>
              )}

              {consignment && sale.owner_paid && (
                <p className="flex items-center gap-2 text-xs font-semibold text-emerald-600">
                  <FiCheckCircle className="h-3.5 w-3.5" /> {car.owner_name} has been paid
                </p>
              )}

              <button
                type="button"
                onClick={() => { setConfirmDelete('sale'); setDeleteError(''); }}
                className="inline-flex items-center gap-1.5 text-xs font-bold text-slate-400 transition hover:text-rose-600"
              >
                <FiRotateCcw className="h-3.5 w-3.5" /> Undo this sale
              </button>
            </div>
          )}
        </div>
      </div>

      {/* What has been spent on it */}
      <div className="mt-5 rounded-2xl border border-slate-200 bg-white shadow-sm">
        <div className="flex items-center justify-between border-b border-slate-100 px-5 py-4">
          <div>
            <h2 className="text-sm font-black uppercase tracking-widest text-slate-400">Spent on this car</h2>
            <p className="mt-0.5 text-xs text-slate-400">
              Cars' own money — it never shows in CAP's daily expenses, it comes off what the car earns.
            </p>
          </div>
          <Button size="sm" variant="secondary" onClick={() => setExpenseOpen(true)}>
            <FiPlus className="mr-1.5 h-3.5 w-3.5" /> Add
          </Button>
        </div>

        {(car.expenses || []).length === 0 ? (
          <p className="px-5 py-8 text-center text-sm text-slate-400">Nothing spent on it yet.</p>
        ) : (
          <ul className="divide-y divide-slate-50">
            {car.expenses.map((e) => (
              <li key={e.id} className="flex items-center gap-3 px-5 py-3">
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-semibold text-slate-800">{e.title}</p>
                  <p className="truncate text-xs text-slate-400">
                    {e.spent_on_display}{e.note ? ` · ${e.note}` : ''}
                  </p>
                </div>
                <span className="shrink-0 text-sm font-bold tabular-nums text-slate-700">{fmtMoney(e.amount)}</span>
                <button
                  type="button"
                  disabled={busy === `expense-${e.id}`}
                  onClick={() => removeExpense(e.id)}
                  className="rounded-lg p-2 text-slate-300 transition hover:bg-rose-50 hover:text-rose-600 disabled:opacity-40"
                  aria-label="Remove"
                >
                  <FiTrash2 className="h-4 w-4" />
                </button>
              </li>
            ))}
            <li className="flex items-center justify-between bg-slate-50/60 px-5 py-3">
              <span className="text-xs font-bold uppercase tracking-wide text-slate-400">Total</span>
              <span className="text-sm font-black tabular-nums text-slate-800">{fmtMoney(pnl.spent_on_car)}</span>
            </li>
          </ul>
        )}
      </div>

      {/* Removing the car itself — only ever possible while it holds no sale */}
      {!sale && (
        <button
          type="button"
          onClick={() => { setConfirmDelete('car'); setDeleteError(''); }}
          className="mt-5 inline-flex items-center gap-1.5 text-xs font-bold text-slate-400 transition hover:text-rose-600"
        >
          <FiTrash2 className="h-3.5 w-3.5" /> Remove this car from the lot
        </button>
      )}

      <SellCarModal
        open={sellOpen}
        onClose={() => setSellOpen(false)}
        car={car}
        onSold={setCar}
        sellFn={carsApi.sell}
      />
      <ExpenseModal open={expenseOpen} onClose={() => setExpenseOpen(false)} car={car} onSaved={setCar} />
      <AddCarModal
        open={editOpen}
        onClose={() => setEditOpen(false)}
        catalog={catalog}
        car={car}
        saveFn={(payload) => carsApi.update(car.id, payload)}
        onSaved={setCar}
      />

      {confirmDelete && (
        <DeleteConfirm
          title={confirmDelete === 'sale' ? 'Undo this sale?' : 'Remove this car?'}
          summary={car.display_name}
          consequences={confirmDelete === 'sale'
            ? ['The car goes back on the lot as available',
               'The sale, its commission and the owner payout are removed',
               'A record of it is kept in Deleted Records']
            : ['The car is removed from the lot',
               'Everything spent on it goes with it',
               'A record of it is kept in Deleted Records']}
          requirePassword
          busy={busy === 'delete'}
          error={deleteError}
          onConfirm={doDelete}
          onClose={() => setConfirmDelete(null)}
        />
      )}
    </>
  );
};

const CarDetailPage = () => (
  <MainLayout>
    <CarDetailBoard />
  </MainLayout>
);

export default CarDetailPage;
